import React, { useState } from "react";
import { Crown, Lock, CheckCircle2, ChevronDown, ChevronUp, TrendingUp } from "lucide-react";

/**
 * Leadership Reward — 10-tier rank system (Explorer → Creator).
 * Displays qualification target, self deposit, team ROI %, and cap per rank
 * with the user's live unlock/lock state and current rank highlighted.
 */
export function LeadershipRewardTable({ data }) {
  const [expanded, setExpanded] = useState(false);

  const ranks = data?.ranks || [];
  const current = data?.current_rank;
  const currentId = data?.current_rank_id || 0;
  const dailyRoiPct = data?.daily_roi_pct || 0;
  const capX = data?.cap_x || 2;
  const cycle = data?.cycle || {};

  if (!data) {
    return (
      <div>
        <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--text-3)] mb-2 px-1">Leadership Reward</div>
        <div className="card-3d lift-on-hover p-6 text-center relative" data-testid="leadership-reward-loading">
          <div className="text-xs text-[var(--text-3)]">Loading leadership status…</div>
        </div>
      </div>
    );
  }

  const visibleRanks = expanded ? ranks : ranks.slice(0, 5);

  return (
    <div data-testid="leadership-reward-section">
      <div className="flex items-center justify-between mb-2 px-1">
        <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--text-3)]">Leadership Reward</div>
        <div className="text-[10px] mono text-[var(--text-3)]">
          {current ? (
            <>
              Rank: <span style={{ color: "#FFD36A" }}>{current}</span>
            </>
          ) : (
            <>
              <span style={{ color: "#FFD36A" }}>{currentId}</span>
              <span className="mx-0.5 opacity-50">/</span>
              <span>{ranks.length}</span>
              <span className="ml-1 opacity-70">unlocked</span>
            </>
          )}
        </div>
      </div>

      <div
        className="card-3d lift-on-hover relative overflow-hidden p-0"
        style={{
          background:
            "linear-gradient(180deg, rgba(18,35,58,0.96) 0%, rgba(8,20,38,0.96) 100%)",
          border: "1px solid rgba(255,211,106,0.34)",
        }}
        data-testid="leadership-reward-card"
      >
        {/* Gold side ornaments */}
        <div
          className="absolute inset-y-0 left-0 w-1.5 pointer-events-none"
          style={{
            background:
              "linear-gradient(180deg, transparent 0%, rgba(255,211,106,0.60) 50%, transparent 100%)",
          }}
        />
        <div
          className="absolute inset-y-0 right-0 w-1.5 pointer-events-none"
          style={{
            background:
              "linear-gradient(180deg, transparent 0%, rgba(255,211,106,0.60) 50%, transparent 100%)",
          }}
        />

        {/* Header — matches image */}
        <div className="px-4 pt-4 pb-2 text-center relative">
          <div
            className="inline-flex items-center gap-2 text-base font-bold tracking-[0.18em]"
            style={{
              color: "#FFD36A",
              textShadow: "0 0 12px rgba(255,211,106,0.40)",
            }}
          >
            <Crown size={16} /> LEADERSHIP REWARD
          </div>
          <div
            className="mt-1 mx-auto h-px"
            style={{
              maxWidth: "240px",
              background:
                "linear-gradient(90deg, transparent 0%, rgba(255,211,106,0.65) 50%, transparent 100%)",
            }}
          />
        </div>

        {/* Live cycle summary (when user has a deposit) */}
        {cycle.self_deposit > 0 && (
          <div className="mx-3 mb-3 grid grid-cols-3 gap-2 text-center">
            <CyclePill
              label="Daily ROI"
              value={dailyRoiPct > 0 ? `${dailyRoiPct}%` : "—"}
              accent={dailyRoiPct > 0}
            />
            <CyclePill
              label="Cap"
              value={`${capX}X`}
              accent
            />
            <CyclePill
              label="Earned / Cap"
              value={`$${(cycle.cycle_earnings || 0).toFixed(0)} / $${(cycle.cycle_cap || 0).toFixed(0)}`}
              accent={cycle.is_capped}
              danger={cycle.is_capped}
            />
          </div>
        )}

        {/* Cap progress bar */}
        {cycle.self_deposit > 0 && (
          <div className="mx-3 mb-3" data-testid="leadership-cap-progress">
            <div className="flex justify-between text-[10px] mb-1">
              <span className="uppercase tracking-[0.14em] text-[var(--text-3)]">Cycle Progress</span>
              <span className="mono" style={{ color: cycle.is_capped ? "#FCA5A5" : "#FFD36A" }}>
                {(cycle.cycle_progress_pct || 0).toFixed(1)}%
              </span>
            </div>
            <div className="h-2 w-full rounded-full bg-white/10 overflow-hidden">
              <div
                className="h-full rounded-full"
                style={{
                  width: `${Math.min(100, cycle.cycle_progress_pct || 0)}%`,
                  background: cycle.is_capped
                    ? "linear-gradient(90deg, #EF4444 0%, #DC2626 100%)"
                    : "linear-gradient(90deg, #FFD36A 0%, #F3BA2F 100%)",
                  transition: "width 320ms ease-out",
                }}
              />
            </div>
            {cycle.is_capped && (
              <div className="text-[10px] mt-1.5" style={{ color: "#FCA5A5" }}>
                ⚠ Cap reached — redeposit to start a new cycle.
              </div>
            )}
          </div>
        )}

        {/* Table header */}
        <div
          className="grid grid-cols-[36px_1fr_60px_55px_45px] gap-2 px-3 py-2 text-[10px] uppercase tracking-[0.10em] font-semibold"
          style={{
            background:
              "linear-gradient(180deg, rgba(255,211,106,0.20) 0%, rgba(255,211,106,0.06) 100%)",
            color: "#FFE5A1",
            borderTop: "1px solid rgba(255,211,106,0.32)",
            borderBottom: "1px solid rgba(255,211,106,0.32)",
          }}
        >
          <div>#</div>
          <div>Rank · Target</div>
          <div className="text-right">Self</div>
          <div className="text-right">ROI</div>
          <div className="text-right">Cap</div>
        </div>

        {/* Rows */}
        <div className="divide-y divide-white/[0.04]" data-testid="leadership-rows">
          {visibleRanks.map((r) => (
            <RankRow key={r.rank_id} r={r} isCurrent={r.name === current} />
          ))}
        </div>

        {/* Expand / collapse */}
        {ranks.length > 5 && (
          <button
            onClick={() => setExpanded((v) => !v)}
            className="w-full flex items-center justify-center gap-2 py-3 text-xs font-semibold transition-colors hover:bg-white/[0.03]"
            style={{
              color: "#FFD36A",
              borderTop: "1px solid rgba(255,211,106,0.22)",
            }}
            data-testid="leadership-toggle"
          >
            {expanded ? (
              <>
                <ChevronUp size={14} /> Show Less
              </>
            ) : (
              <>
                <ChevronDown size={14} /> Show All {ranks.length} Ranks
              </>
            )}
          </button>
        )}
      </div>
    </div>
  );
}

function CyclePill({ label, value, accent, danger }) {
  const bgColor = danger
    ? "linear-gradient(180deg, rgba(239,68,68,0.10) 0%, rgba(239,68,68,0.03) 100%)"
    : accent
    ? "linear-gradient(180deg, rgba(255,211,106,0.10) 0%, rgba(255,211,106,0.02) 100%)"
    : "linear-gradient(180deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0.01) 100%)";
  const borderColor = danger
    ? "rgba(239,68,68,0.40)"
    : accent
    ? "rgba(255,211,106,0.36)"
    : "rgba(255,255,255,0.08)";
  const valueColor = danger ? "#FCA5A5" : accent ? "#FFE5A1" : "var(--text-1)";
  return (
    <div
      className="rounded-lg px-2 py-2 border"
      style={{ background: bgColor, borderColor }}
    >
      <div className="text-[9px] uppercase tracking-[0.10em] text-[var(--text-3)]">{label}</div>
      <div className="mono text-sm font-semibold mt-0.5" style={{ color: valueColor }}>
        {value}
      </div>
    </div>
  );
}

function RankRow({ r, isCurrent }) {
  const {
    rank_id,
    name,
    self_min,
    directs_required,
    direct_rank_required,
    team_size_min,
    legs_required,
    leg_rank_required,
    daily_roi_pct,
    cap_x,
    unlocked,
    missing,
  } = r;

  // Build qualification target label
  let target;
  if (legs_required > 0 && leg_rank_required) {
    target = `${legs_required} ${leg_rank_required} in ${legs_required} Legs`;
  } else {
    const parts = [];
    if (directs_required > 0) {
      if (direct_rank_required) {
        parts.push(`${directs_required} direct ${direct_rank_required}`);
      } else {
        parts.push(`${directs_required} directs $100`);
      }
    }
    if (team_size_min > 0) parts.push(`Team of ${team_size_min}`);
    target = parts.join(" + ");
  }

  const missingLabel = !unlocked && missing && missing.length > 0
    ? missing.map((m) => m.label).join(" + ")
    : null;

  return (
    <div
      className={`grid grid-cols-[36px_1fr_60px_55px_45px] gap-2 px-3 py-2.5 items-center text-xs ${
        isCurrent ? "" : ""
      }`}
      style={
        isCurrent
          ? {
              background:
                "linear-gradient(90deg, rgba(255,211,106,0.10) 0%, rgba(255,211,106,0.02) 100%)",
            }
          : undefined
      }
      data-testid={`leadership-row-${rank_id}`}
    >
      <div
        className="mono font-semibold"
        style={{ color: unlocked ? "#FFD36A" : "var(--text-3)" }}
      >
        {String(rank_id).padStart(2, "0")}
      </div>

      <div className="min-w-0">
        <div
          className="text-[12px] font-semibold inline-flex items-center gap-1"
          style={{ color: isCurrent ? "#FFD36A" : unlocked ? "var(--text-1)" : "var(--text-2)" }}
        >
          {name}
          {isCurrent && (
            <span
              className="px-1.5 py-0.5 rounded-md text-[8px] uppercase tracking-[0.10em] font-bold"
              style={{
                background: "linear-gradient(135deg, #FFD36A 0%, #F3BA2F 100%)",
                color: "#000",
              }}
            >
              Current
            </span>
          )}
          {!isCurrent && unlocked && <CheckCircle2 size={11} style={{ color: "#86EFAC" }} />}
          {!unlocked && <Lock size={11} style={{ color: "var(--text-3)" }} />}
        </div>
        <div className="text-[10px] truncate" style={{ color: "var(--text-3)" }} title={target}>
          {target || "—"}
        </div>
        {missingLabel && (
          <div
            className="text-[9px] truncate mt-0.5"
            style={{ color: "#FCA5A5" }}
            title={missingLabel}
          >
            Need: {missingLabel}
          </div>
        )}
      </div>

      <div
        className="mono text-right text-[11px] font-semibold"
        style={{ color: unlocked ? "#86EFAC" : "var(--text-3)" }}
      >
        ${self_min}
      </div>
      <div
        className="mono text-right text-[11px] font-semibold inline-flex items-center justify-end gap-0.5"
        style={{ color: unlocked ? "#FFE5A1" : "var(--text-3)" }}
      >
        {unlocked && <TrendingUp size={9} />}
        {daily_roi_pct}%
      </div>
      <div
        className="mono text-right text-[11px] font-semibold"
        style={{ color: unlocked ? "#FFE5A1" : "var(--text-3)" }}
      >
        {cap_x}X
      </div>
    </div>
  );
}
